import React from 'react'
import { styled } from '@mui/material/styles'
import { Logger } from 'wdc-cube'
import { classToFComponent, FCClassContext, IconActionButton, LifesHubColors } from 'src/utils/views'
import { TcmCard } from 'src/utils/views/modules/the-consumer-market'

import Tooltip from '@mui/material/Tooltip'
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'

import {
    DataGrid,
    GridColDef,
    GridColumnVisibilityModel,
    GridPaginationModel,
    GridRowClassNameParams,
    GridRowParams,
    GridSortModel
} from '@mui/x-data-grid'

import StyledDataGrid from '../../the-consumer-market/views/helpers/tcm_datagrid'
import { ColumnVisibilityButton } from '../../the-consumer-market/views/helpers/tcm_column_visibility_button'
import { formatNumber } from '../../the-consumer-market/views/helpers/tcm_view_utils'
import {
    EmpresasTableScope,
    EstabelecimentosTableScope,
    type ColumnVisibility,
    type EmpresasTableVariante,
    type PagedTable
} from '../mv_scopes'
import type { EmpresaRow, EstabelecimentoRow } from '../mv_types'
import { renderLink } from './mv_components'

const LOG = Logger.get('MV-Tables')

const CustomDataGrid = StyledDataGrid as typeof DataGrid

const GridBox = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    '& .MuiDataGrid-root': { flexGrow: 1, minHeight: 0 }
})

const PAGE_SIZE_OPTIONS = [25, 50, 100]

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })

const formatInt = (value: unknown) =>
    value === null || value === undefined || value === '' ? '' : formatNumber(Number(value))

const formatBrl = (value: unknown) =>
    value === null || value === undefined || value === '' ? '' : brl.format(Number(value))

const formatDecimal = (value: unknown) =>
    value === null || value === undefined || value === '' ? '' : String(value).replace('.', ',')

function formatCnpj(value: unknown) {
    const v = String(value ?? '').padStart(14, '0')
    return v.length === 14 ? v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5') : String(value ?? '')
}

function formatCep(value: unknown) {
    const v = String(value ?? '')
    return v.length === 8 ? `${v.substring(0, 5)}-${v.substring(5)}` : v
}

function selectedClass(selectedId: string) {
    return (params: GridRowClassNameParams) => (String(params.id) === selectedId ? 'super-app-theme-selected' : '')
}

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Grade paginada no servidor
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

type PagedGridProps<T> = {
    scope: PagedTable<T>
    columns: GridColDef[]
    getRowId: (row: T) => string
    columnVisibilityModel?: GridColumnVisibilityModel
    onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void
}

function PagedGrid<T extends object>({
    scope,
    columns,
    getRowId,
    columnVisibilityModel,
    onColumnVisibilityModelChange
}: PagedGridProps<T>) {
    const sortModel: GridSortModel = scope.sort
        ? [{ field: scope.sort.field, sort: scope.sort.desc ? 'desc' : 'asc' }]
        : []

    const emitPagination = (model: GridPaginationModel) => {
        scope.onPageChange(model.page, model.pageSize).catch(LOG.caught)
    }

    const emitSort = (model: GridSortModel) => {
        const item = model[0]
        scope.onSortChange(item ? { field: item.field, desc: item.sort === 'desc' } : undefined).catch(LOG.caught)
    }

    const emitRowClick = (params: GridRowParams) => {
        scope.onRowClicked(String(params.id)).catch(LOG.caught)
    }

    return (
        <CustomDataGrid
            rows={[...scope.rows]}
            columns={columns}
            getRowId={getRowId}
            density="compact"
            loading={scope.loading}
            rowCount={scope.total}
            paginationMode="server"
            sortingMode="server"
            paginationModel={{ page: scope.page, pageSize: scope.pageSize }}
            onPaginationModelChange={emitPagination}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            sortModel={sortModel}
            onSortModelChange={emitSort}
            onRowClick={emitRowClick}
            getRowClassName={selectedClass(scope.selectedId)}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            disableRowSelectionOnClick
            disableColumnSelector
            disableColumnFilter
        />
    )
}

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Empresas (base CNPJ)
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

const cnpjColumn: GridColDef = {
    field: 'cnpj',
    headerName: 'CNPJ',
    width: 150,
    valueFormatter: (value) => formatCnpj(value)
}

const FONTE_DOMINIO: Record<string, string> = {
    site_2023: 'Site (2023)',
    registro_br: 'Registro.br',
    google: 'Google Maps'
}

const FonteTag = styled('span')({
    borderRadius: 999,
    border: '1px solid var(--border)',
    backgroundColor: 'var(--surface-2, #f7f9f8)',
    padding: '1px 8px',
    fontSize: 11,
    fontWeight: 650,
    color: 'var(--muted)',
    whiteSpace: 'nowrap'
})

const Links = styled('span')({
    display: 'inline-flex',
    gap: 8,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
})

function socialLabel(url: string) {
    const host = url
        .replace(/^[a-z]+:\/\//i, '')
        .replace(/^www\./, '')
        .split('/')[0]
        .toLowerCase()
    if (host.includes('instagram')) return 'Instagram'
    if (host.includes('facebook') || host === 'fb.com') return 'Facebook'
    if (host.includes('wa.me') || host.includes('whatsapp')) return 'WhatsApp'
    if (host.includes('linkedin')) return 'LinkedIn'
    if (host.includes('youtube')) return 'YouTube'
    if (host.includes('tiktok')) return 'TikTok'
    return host
}

const dominioColumn = (width: number): GridColDef => ({
    field: 'dominio',
    headerName: 'Domínio',
    width,
    sortable: false,
    renderCell: ({ value, row }) => (value ? renderLink(row.urlDominio || value, String(value)) : '')
})

const fonteDominioColumn: GridColDef = {
    field: 'fonteDominio',
    headerName: 'Fonte do domínio',
    width: 130,
    sortable: false,
    renderCell: ({ value }) => (value ? <FonteTag>{FONTE_DOMINIO[String(value)] ?? String(value)}</FonteTag> : '')
}

const midiaSocialColumn: GridColDef = {
    field: 'midiaSocial',
    headerName: 'Mídia social',
    width: 200,
    sortable: false,
    renderCell: ({ value }) => {
        const urls = String(value ?? '')
            .split(',')
            .map((u) => u.trim())
            .filter((u) => u)
        if (urls.length === 0) return ''
        const vistos = new Set<string>()
        return (
            <Links>
                {urls
                    .filter((u) => {
                        const label = socialLabel(u)
                        if (vistos.has(label)) return false
                        vistos.add(label)
                        return true
                    })
                    .map((u) => (
                        <React.Fragment key={u}>{renderLink(u, socialLabel(u))}</React.Fragment>
                    ))}
            </Links>
        )
    }
}

const EMPRESA_RESUMO_COLUMNS: GridColDef[] = [
    cnpjColumn,
    { field: 'nome', headerName: 'Nome', minWidth: 220, flex: 1 },
    { field: 'cnae', headerName: 'CNAE', width: 90, sortable: false },
    { field: 'municipio', headerName: 'Município', width: 150 },
    { field: 'uf', headerName: 'UF', width: 60 },
    { field: 'funcionarios', headerName: 'Funcionários', width: 110, type: 'number', valueFormatter: formatInt },
    {
        field: 'faturamento',
        headerName: 'Faturamento Estimado da Empresa',
        width: 150,
        type: 'number',
        valueFormatter: formatBrl
    },
    {
        field: 'faturamentoGrupo',
        headerName: 'Faturamento Estimado do Grupo',
        width: 150,
        type: 'number',
        valueFormatter: formatBrl
    },
    dominioColumn(190),
    fonteDominioColumn,
    { field: 'email', headerName: 'E-mail', width: 200, sortable: false },
    { field: 'telefone', headerName: 'Telefone', width: 140, sortable: false },
    midiaSocialColumn
]

const theConnectionColumn: GridColDef = {
    field: 'theConnection',
    headerName: 'Relações Societárias',
    width: 150,
    sortable: false,
    renderCell: ({ row }) =>
        row.cnpj ? renderLink(`${window.location.origin}/#/theConnection?companyIds=${row.cnpj}`, 'Abrir') : ''
}

const EMPRESA_COMPLETA_COLUMNS: GridColDef[] = [
    cnpjColumn,
    theConnectionColumn,
    { field: 'razaoSocial', headerName: 'Razão Social', width: 260 },
    { field: 'nome', headerName: 'Nome', width: 200, sortable: false },
    {
        field: 'cnpjMatriz',
        headerName: 'CNPJ Matriz',
        width: 150,
        sortable: false,
        valueFormatter: (value) => formatCnpj(value)
    },
    { field: 'nomeMatriz', headerName: 'Nome - Matriz', width: 200, sortable: false },
    { field: 'razaoSocialMatriz', headerName: 'Razão Social - Matriz', width: 220, sortable: false },
    { field: 'ufMatriz', headerName: 'UF - Matriz', width: 90, sortable: false },
    { field: 'cnpjGrupo', headerName: 'CNPJ grupo (8)', width: 120, sortable: false },
    { field: 'naturezaJuridica', headerName: 'Natureza Jurídica', width: 220, sortable: false },
    { field: 'naturezaAgrupamento', headerName: 'Natureza Jurídica - Agrupamento', width: 130, sortable: false },
    { field: 'qualificacaoResponsavel', headerName: 'Qualificação do Responsável', width: 200, sortable: false },
    { field: 'cnae', headerName: 'CNAE', width: 90, sortable: false },
    { field: 'cnaeDescricao', headerName: 'Descrição CNAE', width: 200, sortable: false },
    { field: 'funcionarios', headerName: 'Funcionários', width: 110, type: 'number', valueFormatter: formatInt },
    { field: 'idadeAnos', headerName: 'Idade (anos)', width: 100, type: 'number', valueFormatter: formatInt },
    {
        field: 'empresasGrupo',
        headerName: 'Empresas no grupo',
        width: 130,
        type: 'number',
        sortable: false,
        valueFormatter: formatInt
    },
    { field: 'endereco', headerName: 'Endereço', width: 240, sortable: false },
    { field: 'bairro', headerName: 'Bairro', width: 160, sortable: false },
    { field: 'cep', headerName: 'CEP', width: 100, sortable: false, valueFormatter: (value) => formatCep(value) },
    { field: 'municipio', headerName: 'Município', width: 150 },
    { field: 'uf', headerName: 'UF', width: 60 },
    { field: 'telefone1', headerName: 'Telefone 1 (RF)', width: 130, sortable: false },
    { field: 'telefone2', headerName: 'Telefone 2 (RF)', width: 130, sortable: false },
    { field: 'fax', headerName: 'Telefone Fax (RF)', width: 130, sortable: false },
    { field: 'emailRf', headerName: 'E-mail (RF)', width: 220, sortable: false },
    { field: 'capitalSocial', headerName: 'Capital Social', width: 140, type: 'number', valueFormatter: formatBrl },
    { field: 'socios', headerName: 'Nome dos sócios', width: 280, sortable: false },
    { field: 'inicioAtividade', headerName: 'Início Atividade', width: 120 },
    { field: 'matrizFilial', headerName: 'Matriz/Filial', width: 100, sortable: false },
    { field: 'situacao', headerName: 'Situação Cadastral', width: 130, sortable: false },
    { field: 'situacaoData', headerName: 'Data Situação', width: 110, sortable: false },
    { field: 'motivoSituacao', headerName: 'Motivo da Situação', width: 180, sortable: false },
    { field: 'situacaoEspecial', headerName: 'Situação Especial', width: 150, sortable: false },
    { field: 'filiais', headerName: 'Total de filiais', width: 110, type: 'number', sortable: false },
    {
        field: 'salarioTotal',
        headerName: 'Salário Total',
        width: 140,
        type: 'number',
        sortable: false,
        valueFormatter: formatBrl
    },
    {
        field: 'salarioTotalGrupo',
        headerName: 'Salário Total Grupo',
        width: 150,
        type: 'number',
        sortable: false,
        valueFormatter: formatBrl
    },
    { field: 'faturamento', headerName: 'Faturamento Estimado', width: 150, type: 'number', valueFormatter: formatBrl },
    {
        field: 'faturamentoGrupo',
        headerName: 'Faturamento Estimado Grupo',
        width: 160,
        type: 'number',
        valueFormatter: formatBrl
    },
    { field: 'porte', headerName: 'Porte', width: 160, sortable: false },
    { field: 'optanteSimples', headerName: 'Optante Simples', width: 120, sortable: false },
    dominioColumn(200),
    { field: 'outrosDominios', headerName: 'Outros domínios', width: 200, sortable: false },
    { field: 'emailSite', headerName: 'E-mail (site)', width: 220, sortable: false },
    { field: 'telefoneSite', headerName: 'Telefone (site)', width: 150, sortable: false },
    midiaSocialColumn,
    {
        field: 'siteGoogle',
        headerName: 'Site Google',
        width: 190,
        sortable: false,
        renderCell: ({ value }) => renderLink(value)
    },
    { field: 'nomeGoogle', headerName: 'Nome Google', width: 220, sortable: false },
    { field: 'categoriasGoogle', headerName: 'Categorias Google', width: 180, sortable: false },
    { field: 'notaGoogle', headerName: 'Nota Google', width: 100, type: 'number', valueFormatter: formatDecimal },
    {
        field: 'avaliacoesGoogle',
        headerName: 'Avaliações Google',
        width: 130,
        type: 'number',
        valueFormatter: formatInt
    },
    { field: 'telefoneGoogle', headerName: 'Telefone Google', width: 140, sortable: false },
    {
        field: 'tempoMedioGoogle',
        headerName: 'Tempo médio (Google)',
        width: 150,
        type: 'number',
        sortable: false,
        valueFormatter: formatDecimal
    },
    { field: 'latitude', headerName: 'Latitude', width: 110, type: 'number', sortable: false },
    { field: 'longitude', headerName: 'Longitude', width: 110, type: 'number', sortable: false }
]

const EMPRESA_RESUMO_FIELDS = new Set(EMPRESA_RESUMO_COLUMNS.map((c) => c.field))

const EMPRESA_COLUMNS: Record<EmpresasTableVariante, GridColDef[]> = {
    resumo: [...EMPRESA_RESUMO_COLUMNS, ...EMPRESA_COMPLETA_COLUMNS.filter((c) => !EMPRESA_RESUMO_FIELDS.has(c.field))],
    completa: EMPRESA_COMPLETA_COLUMNS
}

function isVisibleByDefault(variante: EmpresasTableVariante, field: string) {
    return variante === 'completa' || EMPRESA_RESUMO_FIELDS.has(field)
}

const empresaRowId = (row: EmpresaRow) => String(row.id ?? row.cnpj)

type EmpresasTableViewProps = {
    className?: string
    scope: EmpresasTableScope
}

class EmpresasTableViewClass implements FCClassContext<EmpresasTableViewProps> {
    scope!: EmpresasTableScope

    onSyncState({ scope }: EmpresasTableViewProps) {
        this.scope = scope
    }

    get columnVisibilityModel(): ColumnVisibility {
        const { variante, columnVisibility } = this.scope
        const model: ColumnVisibility = {}
        for (const { field } of EMPRESA_COLUMNS[variante]) {
            model[field] = columnVisibility[field] ?? isVisibleByDefault(variante, field)
        }
        return model
    }

    readonly emitColumnVisibilityModel = (model: GridColumnVisibilityModel) => {
        const next: ColumnVisibility = {}
        for (const { field } of EMPRESA_COLUMNS[this.scope.variante]) {
            next[field] = model[field] !== false
        }
        this.scope.onColumnVisibilityChange(next).catch(LOG.caught)
    }

    readonly emitColumnVisibility = (ids: string[], visible: boolean) => {
        const next = this.columnVisibilityModel
        for (const id of ids) {
            next[id] = visible
        }
        this.scope.onColumnVisibilityChange(next).catch(LOG.caught)
    }

    readonly emitExpand = () => this.scope.onExpandClick?.().catch(LOG.caught)
    readonly emitShrunk = () => this.scope.onShrunkClick?.().catch(LOG.caught)

    renderExpandButton() {
        const scope = this.scope
        if (scope.expanded ? !scope.onShrunkClick : !scope.onExpandClick) return undefined
        const label = scope.expanded ? 'Restaurar detalhamento' : 'Maximizar detalhamento'
        return (
            <Tooltip title={label}>
                <IconActionButton
                    size="small"
                    aria-label={label}
                    onClick={scope.expanded ? this.emitShrunk : this.emitExpand}
                >
                    {scope.expanded ? (
                        <CloseFullscreenRoundedIcon fontSize="small" />
                    ) : (
                        <LaunchRoundedIcon fontSize="small" />
                    )}
                </IconActionButton>
            </Tooltip>
        )
    }

    render({ className, scope }: EmpresasTableViewProps) {
        const columns = EMPRESA_COLUMNS[scope.variante]
        const columnVisibilityModel = this.columnVisibilityModel
        const actions = (
            <>
                <ColumnVisibilityButton
                    small
                    columns={columns.map((c) => ({
                        id: c.field,
                        title: c.headerName ?? c.field,
                        visible: columnVisibilityModel[c.field],
                        canHide: true
                    }))}
                    onChange={this.emitColumnVisibility}
                />
                {this.renderExpandButton()}
            </>
        )

        return (
            <TcmCard
                className={className}
                caption={scope.caption}
                count={scope.loaded ? `${formatNumber(scope.total)} empresas` : undefined}
                actions={actions}
            >
                <GridBox>
                    <PagedGrid
                        scope={scope}
                        columns={columns}
                        getRowId={empresaRowId}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={this.emitColumnVisibilityModel}
                    />
                </GridBox>
            </TcmCard>
        )
    }
}

export const EmpresasTableView = classToFComponent(EmpresasTableViewClass, React)

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Estabelecimentos (Google Maps)
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

const Aberto24h = styled('span')({
    color: LifesHubColors.green800,
    fontWeight: 700
})

const ESTABELECIMENTO_COLUMNS: GridColDef[] = [
    { field: 'estabelecimento', headerName: 'Estabelecimento', minWidth: 220, flex: 1 },
    { field: 'categoria', headerName: 'Categoria', width: 170 },
    { field: 'telefone', headerName: 'Telefone', width: 140, sortable: false },
    { field: 'endereco', headerName: 'Endereço', width: 260, sortable: false },
    { field: 'municipio', headerName: 'Município', width: 140 },
    { field: 'uf', headerName: 'UF', width: 60 },
    {
        field: 'instagram',
        headerName: 'Instagram',
        width: 120,
        sortable: false,
        renderCell: ({ value }) => renderLink(value, 'Instagram')
    },
    {
        field: 'website',
        headerName: 'WebSite',
        width: 180,
        sortable: false,
        renderCell: ({ value }) => renderLink(value)
    },
    {
        field: 'whatsapp',
        headerName: 'Whatsapp',
        width: 110,
        sortable: false,
        renderCell: ({ value }) => renderLink(value, 'Whatsapp')
    },
    { field: 'nota', headerName: 'Nota', width: 70, type: 'number', valueFormatter: formatDecimal },
    {
        field: 'aberto24h',
        headerName: 'Aberto 24h',
        width: 95,
        renderCell: ({ value }) => (value ? <Aberto24h>Sim</Aberto24h> : '')
    }
]

const estabelecimentoRowId = (row: EstabelecimentoRow) => String(row.id)

type EstabelecimentosTableViewProps = {
    className?: string
    scope: EstabelecimentosTableScope
}

class EstabelecimentosTableViewClass implements FCClassContext<EstabelecimentosTableViewProps> {
    scope!: EstabelecimentosTableScope

    onSyncState({ scope }: EstabelecimentosTableViewProps) {
        this.scope = scope
    }

    render({ className, scope }: EstabelecimentosTableViewProps) {
        return (
            <TcmCard
                className={className}
                caption={scope.caption}
                count={scope.loaded ? `${formatNumber(scope.total)} estabelecimentos` : undefined}
            >
                <GridBox>
                    <PagedGrid scope={scope} columns={ESTABELECIMENTO_COLUMNS} getRowId={estabelecimentoRowId} />
                </GridBox>
            </TcmCard>
        )
    }
}

export const EstabelecimentosTableView = classToFComponent(EstabelecimentosTableViewClass, React)

export { formatCnpj }
